import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Scale,
  Users,
  Shield,
  ArrowRight,
  CheckCircle,
  DollarSign,
  Briefcase,
  Clock,
  Building2,
  Heart,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  UserCheck,
  Gavel,
  FileCheck,
  MapPin,
  TrendingDown,
  Home,
  BadgeCheck,
} from "lucide-react";

/* ── Animated counter ── */
const AnimatedCounter = ({ end, prefix = "", suffix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const startTime = Date.now();
          const numEnd = parseFloat(String(end).replace(/[^0-9.]/g, ""));
          const tick = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * numEnd));
            if (progress < 1) requestAnimationFrame(tick);
            else setCount(numEnd);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

/* ── FAQ Item ── */
const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-bold text-slate-900 pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#002B7F] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const statesWithReform = [
  { name: "New Jersey", year: "2017" },
  { name: "New York", year: "2020" },
  { name: "California", year: "2021" },
  { name: "Kentucky", year: "2017" },
  { name: "Colorado", year: "2019" },
  { name: "Washington", year: "2022" },
];

const economicImpacts = [
  {
    icon: Briefcase,
    title: "People Keep Their Jobs",
    description: "Workers aren't fired for missing shifts while sitting in jail awaiting trial",
  },
  {
    icon: Building2,
    title: "Businesses Keep Employees",
    description: "Companies retain trained staff instead of costly replacement hiring",
  },
  {
    icon: Heart,
    title: "Families Stay Stable",
    description: "Children keep their parents. Rent gets paid. Lives continue",
  },
  {
    icon: TrendingDown,
    title: "Less Reliance on Social Services",
    description: "Fewer families pushed into poverty by pretrial detention",
  },
  {
    icon: DollarSign,
    title: "More Money in Local Economy",
    description: "Bail bond fees stay with families instead of leaving the community",
  },
  {
    icon: Home,
    title: "Housing Stability",
    description: "People don't lose their homes over an inability to post bail",
  },
];

const faqs = [
  {
    question: "Will crime go up?",
    answer:
      "No. Every state that has implemented pretrial reform has maintained public safety. Studies from New Jersey, New York, California, Kentucky, Colorado, and Washington show no increase in violent crime rates and no increase in failure-to-appear rates after reform.",
  },
  {
    question: "What about repeat offenders?",
    answer:
      "Repeat offenders are NOT automatically released. Cases involving repeat offenses, violent charges, or flight risk are reviewed by a panel of three judges who evaluate the specific risk factors before making a detention decision.",
  },
  {
    question: "How do we ensure people show up to court?",
    answer:
      "Through proven alternatives: regular check-ins, reminders, community supervision, and accountability measures. Studies show these methods are as effective — or more effective — than money bail at ensuring court appearances.",
  },
  {
    question: "Who decides who stays in jail?",
    answer:
      "For cases that require review, a panel of three Nevada judges makes the decision based on structured criteria: flight risk, public safety risk, and strength of evidence. This replaces the current system where a single judge can make a subjective decision.",
  },
  {
    question: "How much does this save?",
    answer:
      "An estimated $91.8 million per year in direct jail costs alone. When you add the $20-50 million per year that families pay in non-refundable bail bond fees, plus reduced social service costs, the total savings exceed $150 million annually.",
  },
  {
    question: "Is this just letting everyone out of jail?",
    answer:
      "Absolutely not. This plan specifically targets non-violent, non-repeat offenses for automatic release. Violent offenders, repeat offenders, and high-risk individuals still face judicial review and can still be detained. The goal is to stop jailing people simply because they're poor.",
  },
];

const BailReformPage = () => {
  return (
    <div data-testid="bail-reform-page" className="pt-16 md:pt-20">
      {/* ── HERO ── */}
      <section className="relative py-16 md:py-24 bg-slate-900">
        <div className="absolute inset-0 bg-[#002B7F]/90" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            Pretrial Justice Reform
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-6 max-w-3xl">
            Fair Pretrial Justice — Safer Communities, Lower Costs
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mb-8">
            End money bail for non-violent, non-repeat offenses — while
            protecting public safety and saving Nevada over $90 million per
            year.
          </p>

          {/* Reassurance banner */}
          <div className="inline-flex items-center gap-3 bg-emerald-600/90 px-6 py-3 mb-8">
            <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
            <span className="text-white font-bold text-sm">
              Crime does NOT increase with this reform — proven in multiple
              states
            </span>
          </div>

          <div className="flex flex-wrap gap-4">
            <a
              href="#how-it-works"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              See How It Works
            </a>
            <a
              href="#the-plan"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Read the Plan
            </a>
          </div>
        </div>
      </section>

      {/* ── THE PROBLEM ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                The System Is Broken
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Right now, Nevadans are sitting in jail for weeks or months —
                not because they're dangerous, but because they don't have the
                financial resources to pay bail.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                A wealthy person charged with the same crime walks free the same
                day. A working family member without resources stays locked up,
                loses their job, their home, and their stability — all before
                they've been found guilty of anything.
              </p>
              <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-6">
                <p className="text-[#002B7F] font-bold">
                  Justice should depend on risk — not wealth, race, or
                  background.
                </p>
              </div>
            </div>

            {/* Stats side */}
            <div className="space-y-4">
              <div className="bg-slate-900 text-white p-8">
                <p className="text-5xl md:text-6xl font-black text-[#D4AF37] mb-2">
                  70%+
                </p>
                <p className="text-white/80">
                  of people in jail are awaiting trial — not convicted
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#F8F9FA] p-6 border border-slate-200">
                  <p className="text-3xl font-black text-[#002B7F] mb-1">~40</p>
                  <p className="text-sm text-slate-600">
                    Average days spent in pretrial detention
                  </p>
                </div>
                <div className="bg-[#F8F9FA] p-6 border border-slate-200">
                  <p className="text-3xl font-black text-red-600 mb-1">87</p>
                  <p className="text-sm text-slate-600">
                    Days for those not released early
                  </p>
                </div>
              </div>
              <div className="bg-red-50 border border-red-200 p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-800 text-sm leading-relaxed">
                    Many of these people are held for <strong>non-violent offenses</strong>.
                    They lose jobs, housing, and family stability — all while
                    legally presumed innocent.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SAFETY REASSURANCE ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              This Does NOT Increase Crime
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              In <strong>every</strong> state that has implemented pretrial
              reform, crime rates did not increase. The data is clear.
            </p>
          </div>

          {/* State cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
            {statesWithReform.map((state) => (
              <div
                key={state.name}
                className="bg-white border border-emerald-200 p-5 text-center hover:border-emerald-400 transition-colors"
              >
                <MapPin className="w-5 h-5 text-emerald-600 mx-auto mb-2" />
                <p className="font-bold text-slate-900 text-sm">{state.name}</p>
                <p className="text-xs text-slate-500">Reformed {state.year}</p>
              </div>
            ))}
          </div>

          {/* Results grid */}
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: "No Increase in Violent Crime",
                color: "text-emerald-600",
                bg: "bg-emerald-50",
                border: "border-emerald-200",
              },
              {
                icon: UserCheck,
                title: "No Increase in Failure to Appear",
                color: "text-emerald-600",
                bg: "bg-emerald-50",
                border: "border-emerald-200",
              },
              {
                icon: BadgeCheck,
                title: "Public Safety Maintained",
                color: "text-emerald-600",
                bg: "bg-emerald-50",
                border: "border-emerald-200",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className={`${item.bg} border ${item.border} p-6 flex items-center gap-4`}
                >
                  <Icon className={`w-8 h-8 ${item.color} flex-shrink-0`} />
                  <p className="font-bold text-slate-900">{item.title}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── HOW THE PLAN WORKS ── */}
      <section id="how-it-works" className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              A Smarter, Safer System
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              How pretrial decisions will work under this plan
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Card 1 – Auto Release */}
            <div className="bg-white/10 border border-emerald-400/40 p-8">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-500 text-white mb-6">
                <CheckCircle className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">
                Automatic Release
              </h3>
              <p className="text-emerald-300 text-xs font-bold uppercase tracking-wider mb-4">
                Non-violent, non-repeat offenses
              </p>
              <ul className="space-y-3">
                {[
                  "Automatically released",
                  "No money bail required",
                  "Optional check-ins if needed",
                  "Court date reminders",
                ].map((item, i) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Card 2 – 3 Judge Panel */}
            <div className="bg-white/10 border border-red-400/40 p-8">
              <div className="w-12 h-12 flex items-center justify-center bg-red-500 text-white mb-6">
                <Gavel className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">
                3-Judge Panel
              </h3>
              <p className="text-red-300 text-xs font-bold uppercase tracking-wider mb-4">
                Violent or high-risk cases
              </p>
              <ul className="space-y-3">
                {[
                  "Panel of 3 judges — not just 1",
                  "Majority vote required",
                  "Evaluates flight risk",
                  "Evaluates public safety risk",
                  "Evaluates strength of evidence",
                ].map((item, i) => (
                  <li key={item} className="flex items-start gap-2">
                    <Scale className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Card 3 – Accountability */}
            <div className="bg-white/10 border border-[#D4AF37]/40 p-8">
              <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37] text-black mb-6">
                <FileCheck className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">
                Accountability
              </h3>
              <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider mb-4">
                Transparent & documented
              </p>
              <ul className="space-y-3">
                {[
                  "All decisions documented",
                  "Transparent criteria applied",
                  "Right to review decisions",
                  "Data publicly reported",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <FileCheck className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-white/80 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE COST ── */}
      <section id="the-plan" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              This System Is Costing You Millions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We are spending nearly $100 million per year to jail people who are
              presumed innocent — while families pay tens of millions more in
              non-refundable bail fees.
            </p>
          </div>

          {/* Big numbers */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-slate-900 p-8 text-center">
              <p className="text-4xl md:text-5xl font-black text-[#D4AF37] mb-2">
                <AnimatedCounter prefix="$" end={406} suffix="M" />
              </p>
              <p className="text-white/80 text-sm">
                Nevada corrections budget
              </p>
            </div>
            <div className="bg-slate-900 p-8 text-center">
              <p className="text-4xl md:text-5xl font-black text-[#D4AF37] mb-2">
                <AnimatedCounter prefix="$" end={102} suffix="/day" />
              </p>
              <p className="text-white/80 text-sm">Cost per inmate per day</p>
            </div>
            <div className="bg-emerald-600 p-8 text-center">
              <p className="text-4xl md:text-5xl font-black text-white mb-2">
                <AnimatedCounter prefix="$" end={91} suffix=".8M" />
              </p>
              <p className="text-white/80 text-sm">
                Potential savings per year
              </p>
            </div>
          </div>

          {/* Additional cost details */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-red-50 border border-red-200 p-6">
              <p className="text-3xl font-black text-red-600 mb-2">
                $20M–$50M
              </p>
              <p className="text-red-800 font-medium mb-2">
                Bail bond fees paid by families every year
              </p>
              <p className="text-red-700 text-sm">
                Non-refundable fees that leave Nevada communities and go to bail
                bond companies — even when charges are dropped.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 p-6">
              <p className="text-3xl font-black text-emerald-600 mb-2">
                $150M+
              </p>
              <p className="text-emerald-800 font-medium mb-2">
                Total estimated annual savings
              </p>
              <p className="text-emerald-700 text-sm">
                Including direct jail costs, bail fees returned to families,
                reduced social services, and maintained employment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── ECONOMIC IMPACT ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Stronger Economy, Stronger Communities
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              When people aren't jailed for being poor, entire communities
              benefit.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {economicImpacts.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="bg-white border border-slate-200 p-6 hover:border-[#D4AF37] transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#002B7F] text-white mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FAIRNESS & BIAS ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                A Fairer System for Everyone
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                The current bail system disproportionately impacts those who can
                least afford it. People of color are more likely to be held
                pretrial and receive higher bail amounts. Men are more likely to
                be detained than women for similar offenses.
              </p>
              <div className="bg-[#002B7F] p-6 text-white">
                <p className="font-bold text-lg">
                  Justice should depend on risk — not wealth, race, or
                  background.
                </p>
              </div>
            </div>

            {/* Comparison table */}
            <div>
              <h3 className="font-bold text-slate-900 mb-4 text-center">
                Why a 3-Judge Panel Matters
              </h3>
              <div className="border border-slate-200 overflow-hidden">
                <div className="grid grid-cols-2">
                  <div className="bg-red-50 p-4 border-b border-r border-slate-200">
                    <p className="font-bold text-red-800 text-sm">
                      Current System
                    </p>
                  </div>
                  <div className="bg-emerald-50 p-4 border-b border-slate-200">
                    <p className="font-bold text-emerald-800 text-sm">
                      New System
                    </p>
                  </div>
                </div>
                {[
                  ["1 judge decides", "3-judge panel decides"],
                  ["Subjective criteria", "Structured, transparent criteria"],
                  ["Higher bias risk", "Reduced bias through consensus"],
                  ["No public accountability", "Documented & reviewable"],
                  ["Wealth determines freedom", "Risk determines outcome"],
                ].map(([old, now], i) => (
                  <div key={i} className="grid grid-cols-2">
                    <div className="p-4 border-b border-r border-slate-200 flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-400 rounded-full flex-shrink-0" />
                      <span className="text-sm text-slate-600">{old}</span>
                    </div>
                    <div className="p-4 border-b border-slate-200 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-700 font-medium">
                        {now}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-center text-sm text-[#002B7F] font-bold mt-4">
                No single decision should determine someone's freedom.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT THIS ACHIEVES ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">
            What This Means for Nevada
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 text-left">
            {[
              "End wealth-based detention",
              "Save $90M+ per year",
              "Reduce jail overcrowding",
              "Improve fairness across race and income",
              "Maintain public safety",
              "Strengthen Nevada's workforce",
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 bg-white/10 p-4"
              >
                <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <span className="text-white font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CLOSING ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            Justice Should Be Fair — Not Based on Your Bank Account
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-4">
            No one should lose their job, their home, or their future because
            they couldn't afford bail.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed mb-4">
            We can protect public safety AND protect fairness.
          </p>
          <p className="text-lg text-[#002B7F] font-bold leading-relaxed">
            We can build a Nevada that works — for everyone.
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Stand Up for Fair Justice in Nevada
          </h2>
          <p className="text-white/70 mb-8">
            This is safer AND smarter — help us make it happen.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Join the Campaign
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact the Campaign
            </Link>
            <Link
              to="/policies/post-justice"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              Post-Justice Reform <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BailReformPage;
